// src/components/dialog/DlgSensorIndoorOutdoorAssignments.tsx

import { PeopleTwoTone, RemoveTwoTone, AddTwoTone } from '@mui/icons-material'
import {
  Avatar,
  Checkbox,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
} from '@mui/material'

import { SensorRecord, SensorId } from '@/types/domain'
import { KARMA_BasicDialog } from '@/types/karma/dialog'

type DlgSensorIndoorOutdoorAssignmentsProps = KARMA_BasicDialog & {
  data: {
    id?: SensorId
    fSensors: SensorRecord[]
    indoorSensor: number[]
    outdoorSensor: number[]
    selectedSensorType: 'indoor' | 'outdoor'
  }
  onChangeIndoor: (_: number[]) => void
  onChangeOutdoor: (_: number[]) => void
}

export const DlgSensorIndoorOutdoorAssignments = ({
  data,
  onClose,
  onChangeIndoor,
  onChangeOutdoor,
  open,
}: DlgSensorIndoorOutdoorAssignmentsProps) => {
  const { fSensors, id, indoorSensor, outdoorSensor, selectedSensorType } = data

  const getSelectedSensors = () =>
    selectedSensorType === 'outdoor' ? outdoorSensor : indoorSensor

  const setSelectedSensors = newSensors =>
    selectedSensorType === 'outdoor'
      ? onChangeOutdoor(newSensors)
      : onChangeIndoor(newSensors)

  return (
    <Dialog maxWidth={'md'} open={open} onClose={onClose}>
      <DialogTitle>
        {selectedSensorType === 'outdoor' ? 'Outdoor' : 'Indoor'}{' '}
        Sensorzuweisung für Sensor ID {id}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <List sx={{ maxHeight: 500, overflow: 'auto' }}>
          {fSensors
            .filter(
              s =>
                s.sensor_type === 'temperature' ||
                s.sensor_type === 'external_temp_1'
            )
            .sort()
            .map(s => {
              const { entity_id, name } = s
              const entityId = Number(entity_id)
              const isSelected = getSelectedSensors().includes(entityId)

              const onClick = () => {
                const updatedSensors = isSelected
                  ? getSelectedSensors().filter(f => f !== entityId)
                  : [...getSelectedSensors(), entityId]

                setSelectedSensors(updatedSensors)
              }

              return (
                <ListItemButton key={entityId} onClick={onClick}>
                  <ListItemAvatar>
                    <Avatar>
                      <PeopleTwoTone />
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={`Name: ${name}`}
                    secondary={name}
                    sx={{ width: 0.7 }}
                  />

                  <ListItemText
                    primary={`ID: ${entityId}`}
                    sx={{ width: 0.3 }}
                  />

                  <Checkbox edge={'end'} checked={isSelected} />
                </ListItemButton>
              )
            })}
        </List>
      </DialogContent>

      <DialogActions>
        {getSelectedSensors().length} Sensoren zugewiesen
        <Tooltip title={'Dem Sensor alle Sensoren entziehen'}>
          <IconButton onClick={() => setSelectedSensors([])} sx={{ ml: 5 }}>
            <RemoveTwoTone />
          </IconButton>
        </Tooltip>
        <Tooltip title={'Dem Sensor alle Sensoren zuweisen'}>
          <IconButton
            onClick={() =>
              setSelectedSensors(fSensors.map(s => Number(s.entity_id)))
            }
          >
            <AddTwoTone />
          </IconButton>
        </Tooltip>
      </DialogActions>
    </Dialog>
  )
}
